import { DateInput, DatePickerInput } from '@mantine/dates';

import {
    Button,
    Modal,
    Input,
    Loader,
    MultiSelect,
    NumberInput,
    PasswordInput,
    rem,
    ScrollArea,
    Select,
    TagsInput,
    Textarea,
    TextInput,
    type MantineTheme,
    type MantineThemeOverride,
} from '@mantine/core';

// Custom Button Variants
export enum ButtonVariant {
    'FILLED' = 'filled',
    'OUTLINED' = 'outlined',
    'SUBTLE' = 'subtle',
    'SUBTLE_ACCENTED' = 'subtle-accented',
    'TRANSPARENT' = 'transparent',
    'FILLED_DESTRUCTIVE' = 'filled-destructive',
    'DEFAULT' = 'default',
    'PRIMARY' = 'primary',
    'UNSTYLED' = 'unstyled',
    'OUTLINED_DESTRUCTIVE' = 'outlined-destructive',
    'ACTION' = 'action',
}

export enum FontSizes {
    xs = '12px',
    sm = '14px',
    base = '16px',
    lg = '18px',
}

export enum FontWeights {
    normal = 400,
    medium = 500,
    semibold = 600,
}

const subtleInputStyles = (theme: MantineTheme) => ({
    input: {
        fontWeight: 500,
        fontSize: 14,
        borderRadius: '8px',
        padding: '0.5rem',
        color: 'rgb(var(--color-gray-800))',
        border: '1px solid',
        borderColor: 'rgb(var(--color-gray-250))',
    },
    label: {
        fontWeight: 500,
        color: 'rgb(var(--color-gray-800))',
        marginBottom: '4px',
        fontSize: '14px',
    },
});

/** The one label declaration every input in the app uses. */
const DATE_FIELD_LABEL = {
    marginBottom: '4px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgb(var(--color-gray-800))',
} as const;

export const getMantine8ThemeOverride = (
    overrides?: Partial<MantineThemeOverride>,
): MantineThemeOverride =>
    ({
        ...overrides,

        /** brand + palette */
        primaryColor: 'brand',
        primaryShade: { light: 6, dark: 6 },
        colors: {
            // Firecrawl-style orange
            brand: [
                '#FFF4ED', // 0
                '#FFE6D9', // 1
                '#FFD0B8', // 2
                '#FFB28E', // 3
                '#FF8F61', // 4
                '#FF6B36', // 5
                '#FF4D10', // 6 (primary)
                '#E84300', // 7
                '#BF3600', // 8
                '#7C2300', // 9
            ],
            // neutral greys that match your existing tokens
            gray: [
                '#FCFCFD',
                '#F7F7F8',
                '#EFEFF0',
                '#E6E8EB',
                '#DDE0E4',
                '#CDD1D5',
                '#B5BAC1',
                '#9AA1AA',
                '#6B7280',
                '#111827',
            ],
            // optional supporting palettes
            red: [
                '#FFF1F0',
                '#FFE1DF',
                '#FFC9C5',
                '#FFA49E',
                '#FF7A73',
                '#FF524A',
                '#F64539',
                '#DA3025',
                '#B22018',
                '#7A140E',
            ],
            green: [
                '#F1FFF6',
                '#E0FFE9',
                '#C4F7D3',
                '#9CEFBA',
                '#6FE29A',
                '#41D179',
                '#23BE63',
                '#189A50',
                '#137540',
                '#0C4A2C',
            ],
            yellow: [
                '#FFF9E6',
                '#FFF2C7',
                '#FFE598',
                '#FFD463',
                '#FFC02D',
                '#FFAD0A',
                '#F39500',
                '#CC7A00',
                '#995A00',
                '#663C00',
            ],
            // keep a blue palette around if your CSS vars expect it
            blu: [
                '#F0F7FF',
                '#E1EEFF',
                '#C9E0FF',
                '#A8CDFF',
                '#85B7FF',
                '#5A97FF',
                '#3A78F6',
                '#2F63DA',
                '#2148A8',
                '#162F6B',
            ],
            ...(overrides?.colors ?? {}),
        },

        fontFamily: 'Inter, sans-serif',
        headings: {
            fontFamily: 'Inter, sans-serif',
            fontWeight: '600',
        },
        spacing: {
            one: rem(1),
            two: rem(2),
            xxs: rem(4),
            xs: rem(8),
            sm: rem(12),
            md: rem(16),
            lg: rem(20),
            xl: rem(24),
            xxl: rem(32),
            '3xl': rem(40),
            '4xl': rem(48),
            '5xl': rem(64),
            '6xl': rem(80),
            '7xl': rem(96),
            '8xl': rem(128),
            '9xl': rem(160),
        },
        radius: {
            rounded: '8px',
        },
        fontSizes: {
            xs: '12px',
            sm: '14px',
            md: '16px',
            lg: '18px',
            xl: '20px',
        },

        components: {
            Button: Button.extend({
                vars: (_theme, props) => {
                    const variant = props.variant;
                    if (
                        variant === ButtonVariant.OUTLINED ||
                        variant === ButtonVariant.DEFAULT
                    ) {
                        return {
                            root: {
                                '--button-bg': 'rgb(var(--color-white))',
                                '--button-color': 'rgb(var(--color-gray-700))',
                                '--button-bd':
                                    '1px solid rgb(var(--color-gray-250))',
                                '--button-hover': 'rgb(var(--color-gray-200))',
                            },
                        };
                    }
                    if (variant === ButtonVariant.SUBTLE) {
                        return {
                            root: {
                                '--button-bg': 'transparent',
                                '--button-color': 'rgb(var(--color-gray-700))',
                                '--button-hover': 'rgb(var(--color-gray-100))',
                                '--button-hover-color':
                                    'rgb(var(--color-gray-800))',
                            },
                        };
                    }
                    if (variant === ButtonVariant.UNSTYLED) {
                        return {
                            root: {
                                '--button-bg': 'transparent',
                                '--button-color': 'rgb(var(--color-gray-600))',
                                '--button-bd': 'none',
                            },
                        };
                    }
                    if (variant === ButtonVariant.SUBTLE_ACCENTED) {
                        return {
                            root: {
                                '--button-bg': 'rgb(var(--color-white))',
                                '--button-color': 'rgb(var(--color-blu-800))',
                                '--button-hover': 'rgb(var(--color-blu-100))',
                            },
                        };
                    }
                    if (variant === ButtonVariant.TRANSPARENT) {
                        return {
                            root: {
                                '--button-bg': 'transparent',
                                '--button-color': 'rgb(var(--color-gray-700))',
                                '--button-hover': 'transparent',
                                '--button-hover-color':
                                    'rgb(var(--color-gray-800))',
                            },
                        };
                    }
                    if (variant === ButtonVariant.FILLED_DESTRUCTIVE) {
                        return {
                            root: {
                                '--button-bg': 'rgb(var(--color-halt-800))',
                                '--button-color': 'rgb(var(--color-white))',
                                '--button-hover': 'rgb(var(--color-halt-850))',
                            },
                        };
                    }
                    if (variant === ButtonVariant.OUTLINED_DESTRUCTIVE) {
                        return {
                            root: {
                                '--button-bg': 'rgb(var(--color-white))',
                                '--button-color': 'rgb(var(--color-gray-700))',
                                '--button-bd':
                                    '1px solid rgb(var(--color-gray-200))',
                                '--button-hover': 'rgb(var(--color-gray-50))',
                            },
                        };
                    }
                    if (variant === ButtonVariant.ACTION) {
                        return {
                            root: {
                                '--button-bg': 'rgba(99, 102, 241, 0.08)',
                                '--button-color': 'rgb(99, 102, 241)',
                                '--button-hover': 'rgba(99, 102, 241, 0.15)',
                                '--button-bd': 'none',
                            },
                        };
                    }
                    // Let Mantine's built-in variants (light, outline, etc.) use native styling
                    if (variant === 'light' || variant === 'outline') {
                        return { root: {} };
                    }
                    // Default/Primary - Light blue style
                    return {
                        root: {
                            '--button-bg': 'rgb(var(--color-blu-100))',
                            '--button-color': 'rgb(var(--color-blu-800))',
                            '--button-hover': 'rgb(var(--color-blu-300))',
                        },
                    };
                },
                styles: (_theme: MantineTheme, props: { variant?: string }) => ({
                    root: {
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: props.variant === ButtonVariant.ACTION ? 500 : 600,
                        borderRadius: '0.5rem',
                        paddingLeft: '0.5rem',
                        paddingRight: '0.5rem',
                        height: '2.25rem',
                        lineHeight: '1.25rem',
                        fontSize: '0.875rem',
                    },
                    label: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        paddingLeft: '0.25rem',
                        paddingRight: '0.25rem',
                    },
                }),
                defaultProps: { size: 'md' },
            }),

            ScrollArea: ScrollArea.extend({
                styles: {
                    thumb: { backgroundColor: 'rgb(var(--color-gray-300))' },
                    scrollbar: { backgroundColor: 'transparent' },
                },
            }),

            Tooltip: {
                defaultProps: {
                    openDelay: 200,
                    withinPortal: true,
                    withArrow: true,
                    multiline: true,
                    maw: 250,
                    fz: 'xs',
                },
            },
            Popover: {
                defaultProps: {
                    withinPortal: true,
                    radius: 'md',
                    shadow: 'sm',
                },
            },
            Paper: {
                defaultProps: { radius: 'md', shadow: 'xs', withBorder: true },
            },
            Loader: Loader.extend({
                defaultProps: {
                    loaders: { ...Loader.defaultLoaders, dots: Loader },
                },
            }),

            TextInput: TextInput.extend({
                styles: (theme) => ({
                    label: {
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'rgb(var(--color-gray-800))',
                    },
                    input: {
                        // Longhand padding so Mantine's reserved `padding-inline-start`
                        // for `leftSection` is not clobbered by the `padding` shorthand.
                        paddingBlock: '0.5rem',
                        paddingInlineEnd: '0.5rem',
                        borderRadius: '8px',
                        background: '#FFF',
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        fontSize: '14px',
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 500,
                        caretColor: 'var(--mantine-color-brand-6)',
                        '&:hover': {
                            borderColor: 'rgb(var(--color-gray-400))',
                        },
                        '&:focus': {
                            borderColor: 'var(--mantine-color-brand-6)',
                        },
                        '&::placeholder': {
                            color: 'rgb(var(--color-gray-500))',
                            fontWeight: 400,
                        },
                        '&[readonly]': {
                            background: 'rgb(var(--color-gray-50))',
                            border: 'none',
                        },
                        width: '100%',
                    },
                    wrapper: { marginTop: '0rem' },
                    description: { marginBottom: '4px' },
                }),
            }),

            Textarea: Textarea.extend({
                styles: {
                    label: {
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'rgb(var(--color-gray-800))',
                    },
                    input: {
                        borderRadius: '8px',
                        background: 'white',
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        fontSize: '14px',
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 500,
                        '&:hover': {
                            borderColor: 'rgb(var(--color-gray-400))',
                        },
                        '&:focus': {
                            outline: 'none',
                            borderColor: 'var(--mantine-color-brand-6)',
                        },
                        '&::placeholder': {
                            color: 'rgb(var(--color-gray-500))',
                            fontWeight: 400,
                        },
                    },
                },
            }),

            Input: Input.extend({
                styles: {
                    input: {
                        borderRadius: '8px',
                        background: 'white',
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        fontSize: '14px',
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 500,
                        '&:hover': {
                            borderColor: 'rgb(var(--color-gray-400))',
                        },
                        '&:focus': {
                            outline: 'none',
                            borderColor: 'var(--mantine-color-brand-6)',
                        },
                        '&::placeholder': {
                            color: 'rgb(var(--color-gray-500))',
                            fontWeight: 400,
                        },
                    },
                },
            }),

            NumberInput: NumberInput.extend({
                styles: {
                    label: {
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'rgb(var(--color-gray-800))',
                    },
                    /*
                     * Deliberately the same declaration as TextInput, Textarea, Select and
                     * MultiSelect rather than something merely close.
                     *
                     * This was the only input in the theme with a near-invisible
                     * rgba(0,0,0,0.06) border and a drop shadow. Because a shadow paints
                     * outside the border box, a NumberInput standing next to a Select in the
                     * same row read as a taller, softer box even though both are one
                     * --input-height-sm tall.
                     */
                    input: {
                        borderRadius: '8px',
                        background: 'white',
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        fontSize: '14px',
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 500,
                        '&:focus': { outline: 'none' },
                    },
                },
            }),

            PasswordInput: PasswordInput.extend({
                styles: {
                    label: {
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'rgb(var(--color-gray-800))',
                    },
                    input: {
                        width: '100%',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        background: '#FFF',
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        fontSize: '14px',
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 500,
                        caretColor: 'var(--mantine-color-brand-6)',
                        '&::placeholder': {
                            color: 'rgb(var(--color-gray-500))',
                            fontWeight: 400,
                        },
                    },
                    innerInput: {
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '8px',
                        background: '#FFF',
                    },
                    visibilityToggle: {
                        height: '2.25rem',
                        width: '2.25rem',
                        marginRight: 0,
                        backgroundColor: 'transparent',
                        borderRadius: '0 8px 8px 0',
                        '&:hover svg': {
                            color: 'rgb(var(--color-gray-800))',
                            backgroundColor: 'transparent',
                        },
                        '&:focus, &[dataFocusVisible]': { outline: 'none' },
                    },
                },
            }),

            /**
             * Date fields, styled to match every other input.
             *
             * These come from @mantine/dates rather than @mantine/core, so the theme's input
             * entries never reached them: a date field rendered Mantine's stock label — a
             * different size, weight and colour, with different spacing — beside fields that
             * had been themed. In a row of five that reads as two of them being emphasised for
             * a reason nobody can name, and the taller default label pushed the whole field a
             * few pixels out of line with its neighbours.
             *
             * Same declaration as TextInput deliberately, rather than something merely close:
             * these sit next to text inputs in every form that has one.
             */
            /**
             * A modal always sits above a drawer.
             *
             * Mantine gives both the same default layer, so a modal opened FROM a drawer — the
             * ordinary way to act on a row in one — landed under the drawer's overlay: the form
             * you are meant to fill rendered dimmed while the panel behind it stayed bright,
             * which reads as the form being disabled.
             *
             * Set once here rather than at each call site, because every screen that opens a
             * modal from a drawer would otherwise have to rediscover it.
             */
            Modal: Modal.extend({
                defaultProps: { zIndex: 300 },
            }),

            DateInput: DateInput.extend({
                styles: {
                    label: DATE_FIELD_LABEL,
                    description: { marginBottom: '4px' },
                },
            }),

            DatePickerInput: DatePickerInput.extend({
                styles: {
                    label: DATE_FIELD_LABEL,
                    description: { marginBottom: '4px' },
                },
            }),

            Select: Select.extend({
                // Left-aligned under the input: Mantine centres the dropdown, and with the
                // 268px cap below a wide field's list then sits off to the right.
                defaultProps: { comboboxProps: { position: 'bottom-start' } },
                styles: {
                    label: {
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'rgb(var(--color-gray-800))',
                    },
                    input: {
                        borderRadius: '8px',
                        background: 'white',
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        fontSize: '14px',
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 500,
                        '&:focus': { outline: 'none' },
                    },
                    dropdown: { maxWidth: '268px', borderRadius: '8px' },
                },
            }),

            MultiSelect: MultiSelect.extend({
                // Left-aligned under the input: Mantine centres the dropdown, and with the
                // 268px cap below a wide field's list then sits off to the right.
                defaultProps: { comboboxProps: { position: 'bottom-start' } },
                styles: {
                    label: {
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'rgb(var(--color-gray-800))',
                    },
                    input: {
                        borderRadius: '8px',
                        background: 'white',
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        fontSize: '14px',
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 500,
                        '&:focus': { outline: 'none' },
                    },
                    dropdown: { maxWidth: '268px', borderRadius: '8px' },
                },
            }),

            TagsInput: TagsInput.extend({
                vars: (theme, props) =>
                    props.variant === 'subtle' ? subtleInputStyles(theme) : {},
            }),

            ...overrides?.components,
        },
    }) satisfies MantineThemeOverride;
